import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DataProtection, Header } from '@aptsi-types';
import { DataAssetMappingService } from '../shared/services/data-protection-program.service';
import { Router } from '@angular/router';
import { DataProtectionProgramCompletedViewComponent } from './data-protection-program-completed-view/data-protection-program-completed-view.component';
import { MatDialog } from '@angular/material/dialog';

 type DataProtectionProgramRecord = DataProtection & Record<string, unknown>;

type ProgramPaginationMeta = Pick<
  DataProtection,
  'totalMappings' | 'totalPages' | 'currentPage'
>;

interface NormalizedProgramResponse {
  rows: DataProtection[];
  metadata?: ProgramPaginationMeta;
}

@Component({
  selector: 'app-data-protection-program',
  templateUrl: './data-protection-program.component.html',
  styleUrls: ['./data-protection-program.component.scss']
})
export class DataProtectionProgramComponent implements OnInit {
  readonly itemsPerPage = 10;

  currentPage = 1;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;
  issearch = false;

  searchText = '';
  filterByHeader = 'all';

  programRecords: DataProtection[] = [];
  searchedProgramRecords: DataProtection[] = [];

  private cachedSource: DataProtection[] | null = null;
  private cachedTableData: DataProtection[] = [];
  /** Prevents older overlapping page/search requests from replacing newer results. */
  private requestSequence = 0;

  readonly headers: Header[] = [
    {
      name: 'Data Asset ID',
      property: 'dataAssetId',
      showInDropdown: true,
      description: 'Unique identifier of the associated data asset'
    },
    {
      name: 'Data Asset Name',
      property: 'dataAssetName',
      showInDropdown: true,
      description: 'Name of the associated data asset'
    },
     {
      name: 'Mapping ID',
      property: 'mappingId',
      showInDropdown: true,
      description: 'Unique identifier of the associated data asset mapping'
    },

    {
      name: 'Status',
      property: 'suggestedUiStatus',
      showInDropdown: true,
      description: 'Consolidated user-interface status for the program'
    }
  ];


  // readonly buttonConfig = {

  //   update: true,
  //   view: true,
  //   startProgram : true,
  //   delete: false,
  // };


    // This listing screen is read-only. Keep app-main-table enabled while
  // suppressing action buttons until program detail routes are wired in.
  readonly buttonConfig = (row: any): Record<string, boolean> => {
  const status = String(row?.suggestedUiStatus ?? '')
    .trim()
    .toUpperCase();

  if (status === 'PROGRAM_COMPLETED') {
    return {
      view: true,
      startProgram: false,
      update: false,
      delete: false
    };
  }

  if (status === 'PROGRAM_NOT_CREATED') {
    return {
      view: false,
      startProgram: true,
      update: false,
      delete: false
    };
  }

  // For every other status, show only Update.
  return {
    view: false,
    startProgram: false,
    update: true,
    delete: false
  };
};

  constructor(
    private readonly dataProtectionProgramService: DataAssetMappingService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly router: Router,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    void this.fetchMappedDataAssetsWithProgramStatus();
  }

  get displayedData(): DataProtection[] {
    return this.issearch ? this.searchedProgramRecords : this.programRecords;
  }

  get currentTotalItems(): number {
    return this.issearch ? this.totalItemsSearch : this.totalItemsNormal;
  }

  get currentTotalPages(): number {
    return this.issearch ? this.totalPagesSearch : this.totalPagesNormal;
  }

  /** Prepare stable, display-friendly rows for the shared table component. */
  get tableData(): DataProtection[] {
    const source = this.displayedData;
    if (source === this.cachedSource) {
      return this.cachedTableData;
    }

    this.cachedTableData = source.map((program) => {
      const row: Record<string, unknown> = {
        ...(program as DataProtectionProgramRecord)
      };

      for (const header of this.headers) {
        const value = row[header.property];
        row[header.property] =
          value === null || value === undefined || String(value).trim() === ''
            ? '—'
            : value;
      }

      return row as unknown as DataProtection;
    });

    this.cachedSource = source;
    return this.cachedTableData;
  }

  async fetchMappedDataAssetsWithProgramStatus(): Promise<void> {
    const requestId = ++this.requestSequence;
    this.isLoading = true;
    this.isSearching = false;
    this.loadError = false;
    this.searchError = false;

    try {
      const apiResponse: unknown = await this.dataProtectionProgramService
        .FetchMappedDataAssetsWithProgramStatus(this.currentPage - 1)
        .toPromise();

      if (requestId !== this.requestSequence) {
        return;
      }

      const normalized = this.normalizeResponse(apiResponse);
      this.programRecords = normalized.rows;
      this.totalItemsNormal = this.readNonNegativeNumber(
        normalized.metadata?.totalMappings,
        normalized.rows.length,
      );
      // The backend is authoritative for total pages. Do not calculate this locally.
      this.totalPagesNormal = this.readNonNegativeNumber(
        normalized.metadata?.totalPages,
        0,
      );

      if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesNormal) {
        // Results may have shrunk while the user was on a later page.
        this.currentPage = this.totalPagesNormal;
        await this.fetchMappedDataAssetsWithProgramStatus();
        return;
      }

      this.cachedSource = null;
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error fetching mapped data assets with program status:', error);
      this.loadError = true;
      this.programRecords = [];
      this.totalItemsNormal = 0;
      this.totalPagesNormal = 0;
      this.cachedSource = null;
    } finally {
      if (requestId === this.requestSequence) {
        this.isLoading = false;
        this.refreshView();
      }
    }
  }

  async searchPrograms(): Promise<void> {
    const query = this.searchText.trim();
    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.issearch = true;
    this.currentPage = 1;
    this.searchedProgramRecords = [];
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.cachedSource = null;
    await this.fetchMappedDataAssetsWithProgramStatusSearch();
  }

  async fetchMappedDataAssetsWithProgramStatusSearch(): Promise<void> {
    const query = this.searchText.trim();
    if (!query) {
      await this.clearSearch();
      return;
    }

    const requestId = ++this.requestSequence;
    this.issearch = true;
    this.isLoading = false;
    this.isSearching = true;
    this.searchError = false;

    try {
      const apiResponse: unknown = await this.dataProtectionProgramService
        .FetchMappedDataAssetsWithProgramStatusSearch(
          this.currentPage - 1,
          query,
          this.filterByHeader,
        )
        .toPromise();

      if (requestId !== this.requestSequence) {
        return;
      }

      const normalized = this.normalizeResponse(apiResponse);
      this.searchedProgramRecords = normalized.rows;
      this.totalItemsSearch = this.readNonNegativeNumber(
        normalized.metadata?.totalMappings,
        normalized.rows.length,
      );
      // Use the backend page count directly for server-side search results too.
      this.totalPagesSearch = this.readNonNegativeNumber(
        normalized.metadata?.totalPages,
        0,
      );

      if (this.totalPagesSearch === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesSearch) {
        this.currentPage = this.totalPagesSearch;
        await this.fetchMappedDataAssetsWithProgramStatusSearch();
        return;
      }

      this.cachedSource = null;
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error searching mapped data assets with program status:', error);
      this.searchError = true;
      this.searchedProgramRecords = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      this.cachedSource = null;
    } finally {
      if (requestId === this.requestSequence) {
        this.isSearching = false;
        this.refreshView();
      }
    }
  }

  async clearSearch(): Promise<void> {
    this.searchText = '';
    this.issearch = false;
    this.searchError = false;
    this.currentPage = 1;
    this.searchedProgramRecords = [];
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.cachedSource = null;
    await this.fetchMappedDataAssetsWithProgramStatus();
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.searchError = false;
    if (this.searchText.trim()) {
      this.issearch = true;
      this.searchedProgramRecords = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      void this.fetchMappedDataAssetsWithProgramStatusSearch();
    } else if (this.issearch) {
      void this.clearSearch();
    }
  }

  changePage(page: number): void {
    const activeTotalPages = this.issearch ? this.totalPagesSearch : this.totalPagesNormal;
    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > activeTotalPages ||
      page === this.currentPage
    ) {
      return;
    }

    this.currentPage = page;
    if (this.issearch) {
      void this.fetchMappedDataAssetsWithProgramStatusSearch();
    } else {
      void this.fetchMappedDataAssetsWithProgramStatus();
    }
  }

  retryLoad(): void {
    if (this.issearch && this.searchText.trim()) {
      void this.fetchMappedDataAssetsWithProgramStatusSearch();
    } else {
      void this.fetchMappedDataAssetsWithProgramStatus();
    }
  }

  private normalizeResponse(raw: unknown): NormalizedProgramResponse {
    if (Array.isArray(raw)) {
      const rows = raw
        .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
        .map((item) => this.normalizeRow(item));

      return {
        rows,
        metadata: this.findMetadata(raw)
      };
    }

    if (raw && typeof raw === 'object') {
      const wrapper = raw as Record<string, unknown>;
      for (const key of [
        'data',
        'items',
        'content',
        'results',
        'records',
        'programs',
        'mappedDataAssets',
        'Resource retrieved successfully'
      ]) {
        if (Array.isArray(wrapper[key])) {
          const rawRows = wrapper[key] as unknown[];
          const rows = rawRows
            .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
            .map((item) => this.normalizeRow(item));

          return {
            rows,
            metadata:
              this.readMetadata(wrapper['metadata'] ?? wrapper['meta'] ?? wrapper) ??
              this.findMetadata(rawRows)
          };
        }
      }

      return { rows: [], metadata: this.readMetadata(wrapper) };
    }

    console.warn('[DataProtectionProgram] Unrecognized API response structure:', raw);
    return { rows: [] };
  }

  private normalizeRow(item: Record<string, unknown>): DataProtection {
    return { ...item } as unknown as DataProtection;
  }

  private findMetadata(rows: unknown[]): ProgramPaginationMeta | undefined {
    const item = rows.find((candidate) => {
      if (!candidate || typeof candidate !== 'object') {
        return false;
      }
      const row = candidate as Record<string, unknown>;
      return row['totalMappings'] !== undefined ||
        row['totalPages'] !== undefined ||
        row['currentPage'] !== undefined;
    });

    return item ? this.readMetadata(item) : undefined;
  }

  private readMetadata(value: unknown): ProgramPaginationMeta | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    const record = value as Record<string, unknown>;
    const metadata: ProgramPaginationMeta = {};

    if (record['totalMappings'] !== undefined) {
      metadata.totalMappings = this.readNonNegativeNumber(record['totalMappings'], 0);
    }
    if (record['totalPages'] !== undefined) {
      metadata.totalPages = this.readNonNegativeNumber(record['totalPages'], 0);
    }
    if (record['currentPage'] !== undefined) {
      metadata.currentPage = this.readNonNegativeNumber(record['currentPage'], 0);
    }

    return Object.keys(metadata).length ? metadata : undefined;
  }

    handleAction(event: {
    action: string;
    rowIndex: number;
    rowData: DataProtection;
  }): void {

  
   if(event.action === 'startProgram'){
      this.router.navigate(["data-protection/auth-tools/data-protection-program/process",event.rowData.mappingId])
    }
  
      else if(event.action?.toLowerCase() === 'update'){
     this.router.navigate(["data-protection/auth-tools/data-protection-program/process",event.rowData.mappingId])
    }
    else if (event.action?.toLowerCase() === 'view') {
      const mappingId = String(event.rowData?.mappingId ?? '').trim();
      if (!mappingId) {
        console.error('Cannot view completed program: mappingId is missing.', event.rowData);
        return;
      }

      this.dialog.open(DataProtectionProgramCompletedViewComponent, {
        width: '960px',
        maxWidth: '96vw',
        maxHeight: '92vh',
        autoFocus: false,
        restoreFocus: true,
        panelClass: 'completed-program-view-dialog-panel',
        data: {
          mappingId,
          dataAssetName: event.rowData.dataAssetName ?? '',
          dataAssetId: event.rowData.dataAssetId ?? ''
        }
      });
    }
  }

  private readNonNegativeNumber(value: unknown, fallback: number): number {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
  }


  private refreshView(): void {
    this.changeDetector.detectChanges();
  }
}
